import React, { useMemo, useEffect } from 'react';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import {
  ServiceData,
  SlotServices,
  FormInputs,
} from '../../../../utils/state/types';
import { FormStatus } from '../../../../types/form-state';
import { BuyPlanButton } from './BuyPlanButton';
import { BookButton } from './BookButton';
import { AddCartButton } from './AddCartButton';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';
import { CreateBookingErrorType, FormError } from '../../../../types/errors';
import {
  checkDifferentBuyPlans,
  getExceedLimitedPricePlans,
  getSelectedServicesForBuyPlan,
} from '../../../../utils/payment';
import {
  SectionMessage,
  SectionMessageType,
} from '../SectionMessage/SectionMessage';
import { classes } from './ButtonContainer.st.css';
import { getErrorByType } from '../../../../utils/errors/errors';
import { CartFlow } from '../../../../types/types';
import { useFormActions } from '../../Hooks/useFormActions';
import { shouldDisplayCheckoutWarning } from '../../../../utils/payment/shouldDisplayCheckoutWarning/shouldDisplayCheckoutWarning';
import { ExperimentsConsts } from '../../../../consts/experiments';

export interface ButtonContainerProps {
  serviceData: ServiceData;
  status: FormStatus;
  formInputs?: FormInputs;
  errors: FormError[];
  isCart?: boolean;
  isServiceInCart?: boolean;
}

interface AlertMessageProps {
  slotServices: SlotServices;
  formInputs?: FormInputs;
}

interface ErrorMessageProps {
  errors: FormError[];
}

const ButtonsContainer: React.FC<ButtonContainerProps> = ({
  serviceData,
  isCart,
  status,
  isServiceInCart,
  errors,
  formInputs,
}) => {
  const { isUpsellPluginInstalled } = useFormComponentContext();

  const slotServicesWithBuyPlans = getSelectedServicesForBuyPlan(
    serviceData.slotServices,
  );
  const exceedLimitedPricePlans = getExceedLimitedPricePlans(
    serviceData.slotServices,
    formInputs,
  );
  let buttons;

  if (slotServicesWithBuyPlans.length || exceedLimitedPricePlans.length) {
    buttons = (
      <BuyPlanButton slotServices={serviceData.slotServices} status={status} />
    );
  } else if (isCart) {
    buttons = (
      <>
        <AddCartButton status={status} />
        {!isServiceInCart && !isUpsellPluginInstalled && (
          <BookButton
            slotServices={serviceData.slotServices}
            cartFlow={CartFlow.CHECKOUT}
            status={status}
            primary={false}
          />
        )}
      </>
    );
  } else {
    buttons = (
      <BookButton slotServices={serviceData.slotServices} status={status} />
    );
  }

  return (
    <div className={classes.root}>
      <RenderAlertMessage
        slotServices={serviceData.slotServices}
        formInputs={formInputs}
      />
      <RenderErrorMessage errors={errors} />
      <div className={classes.buttons}>{buttons}</div>
    </div>
  );
};

const RenderAlertMessage: React.FC<AlertMessageProps> = ({
  slotServices,
  formInputs,
}) => {
  const { t } = useTranslation();
  const { onConflictAlertShown } = useFormActions();
  const { experiments } = useExperiments();

  const isShowNewBookingPolicySectionEnabled = experiments.enabled(
    ExperimentsConsts.ShowNewBookingPolicySection,
  );

  let alertText: string = '';

  const isDifferentBuyPlan = checkDifferentBuyPlans(slotServices);
  const exceedPlans = getExceedLimitedPricePlans(slotServices, formInputs);

  if (
    shouldDisplayCheckoutWarning(slotServices) &&
    !isShowNewBookingPolicySectionEnabled
  ) {
    alertText = t('app.bookings-form.booking-details.cancellation-fee-warning');
  } else if (exceedPlans?.length) {
    const planNames = exceedPlans.map((plan) => plan.label).join(', ');
    alertText = t(
      'app.bookings-form.right-side-alert.not-enough-sessions-left-price-plan',
      { planNames },
    );
  } else if (isDifferentBuyPlan) {
    alertText = t('app.bookings-form.right-side-alert.too-many-plans');
  }

  useEffect(() => {
    if (alertText) {
      onConflictAlertShown(alertText);
    }
  }, [alertText]);

  if (!alertText) {
    return null;
  }

  return (
    <SectionMessage
      type={SectionMessageType.Alert}
      text={alertText}
      withoutIcon={true}
    />
  );
};

const RenderErrorMessage: React.FC<ErrorMessageProps> = ({ errors }) => {
  const { t } = useTranslation();

  const error = getErrorByType({
    errorType: CreateBookingErrorType,
    errors,
  });

  const getErrorMessage = useMemo(() => {
    if (error) {
      switch (error.errorType) {
        case CreateBookingErrorType.SESSION_CAPACITY_EXCEEDED:
        case CreateBookingErrorType.SCHEDULE_CAPACITY_EXCEEDED:
          return t('app.server-errors.not-enough-spots-left');
        case CreateBookingErrorType.VIOLATIONS_WITH_ERROR_SEVERITY:
          return error.params?.message;
        default:
          return t('app.server-errors.slot-not-available');
      }
    }
    return '';
  }, [error]);

  return (
    <SectionMessage type={SectionMessageType.Error} text={getErrorMessage} />
  );
};

export default ButtonsContainer;
